import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Headphones, Mic2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/Navbar";

export function AuthCallback() {
  const nav = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sid })
      .then(({ data }) => { setUser(data); window.history.replaceState(null, "", "/dashboard"); nav("/dashboard", { replace: true }); })
      .catch(() => { toast.error("Google sign-in failed"); nav("/login", { replace: true }); });
  }, [location.hash, nav, setUser]);
  return null;
}

export default function Login() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user, setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", name: "", role: "artist" });
  const [busy, setBusy] = useState(false);
  const next = params.get("next") || "/dashboard";
  useEffect(() => { if (user) nav(next, { replace: true }); }, [user, nav, next]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = mode === "login" ? { email: f.email, password: f.password } : f;
      const { data } = await api.post(`/auth/${mode === "login" ? "login" : "register"}`, body);
      setUser(data);
      toast.success(`Welcome${mode === "login" ? " back" : ""}, ${data.name}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div data-testid="login-page" className="min-h-[70vh] grid lg:grid-cols-2 gap-10 items-center">
      <div className="hidden lg:block relative h-[560px] rounded-3xl overflow-hidden">
        <img src="https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-[var(--hub)]/20" />
        <div className="absolute bottom-8 left-8 right-8"><div className="font-display text-5xl font-black leading-none">Your sound.<br />Your stage.</div><p className="text-zinc-300 mt-3">Upload, master, promote and get paid — all in one place.</p></div>
      </div>
      <div className="max-w-md w-full mx-auto reveal">
        <Logo />
        <h1 className="font-display text-4xl font-black mt-8">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
        <button data-testid="google-login-button" onClick={google} className="w-full h-12 mt-8 rounded-full bg-white text-black font-semibold flex items-center justify-center gap-3 hover:bg-zinc-200">
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
          Continue with Google
        </button>
        <div className="flex items-center gap-3 my-6 text-xs font-mono text-zinc-600"><div className="h-px flex-1 bg-white/10" />OR EMAIL<div className="h-px flex-1 bg-white/10" /></div>
        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (<>
            <Input data-testid="register-name-input" placeholder="Artist or display name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required className="h-12 bg-white/5 border-white/10" />
            <div className="grid grid-cols-2 gap-2">
              {[["artist", Mic2, "I'm an artist"], ["listener", Headphones, "I'm a listener"]].map(([r, Icon, label]) => (
                <button type="button" key={r} data-testid={`register-role-${r}`} onClick={() => setF({ ...f, role: r })} className={`h-14 rounded-xl border flex items-center justify-center gap-2 text-sm font-semibold ${f.role === r ? "border-[var(--hub)] bg-[var(--hub)]/10" : "border-white/10"}`}><Icon size={16} />{label}</button>
              ))}
            </div>
          </>)}
          <Input data-testid="login-email-input" type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required className="h-12 bg-white/5 border-white/10" />
          <Input data-testid="login-password-input" type="password" placeholder="Password (min 6 characters)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required minLength={6} className="h-12 bg-white/5 border-white/10" />
          <button data-testid="login-form-submit-button" disabled={busy} className="w-full h-12 rounded-full bg-[var(--hub)] text-white font-semibold disabled:opacity-50">{mode === "login" ? "Sign in" : "Create account"}</button>
        </form>
        <button data-testid="auth-mode-toggle" onClick={() => setMode(mode === "login" ? "register" : "login")} className="mt-5 text-sm text-zinc-400 hover:text-white">{mode === "login" ? "New to Soundra? Create an account" : "Already have an account? Sign in"}</button>
        <p className="text-xs text-zinc-600 mt-6">Apple ID and Magic Link sign-in are coming soon.</p>
      </div>
    </div>
  );
}
